import React, { useState } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { Files, Link2, Mail } from 'lucide-react';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { CocoEvent, useAnalytics } from '@cotera/client/app/etc';
import type { ResourceActiveShareLink } from '@cotera/client/app/etc/api/typed-client';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { Button } from '@cotera/client/v0/components/ui/button';
import { permissionLabel, toSharePermission } from './share-permission-select';
import { audienceLabel } from './share-link-settings';
import type { ShareTarget } from './share-targets';
import {
  apiErrorMessage,
  useActiveShareLinks,
  useApplyShareChange,
} from './use-share-access';

/**
 * Every live link on a resource, from anyone who made one — a view inside the
 * share panel, reached from the link card. Turning a link off stops new people
 * getting in through it; people who already opened it are in the share list
 * and are removed there, one by one — the same as Docs.
 */
export const ShareActiveLinks: React.FC<{ target: ShareTarget }> = ({
  target,
}) => {
  const linksQuery = useActiveShareLinks(target, true);
  const links = linksQuery.data ?? [];

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        Turning a link off stops new people from opening it. Anyone who already
        joined keeps access until you remove them.
      </p>
      {linksQuery.isPending ? (
        <div className="flex justify-center py-8">
          <Loading.Spinner variant="sm" />
        </div>
      ) : links.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No active links.
        </p>
      ) : (
        <ul className="flex max-h-[50vh] flex-col divide-y divide-border overflow-y-auto">
          {links.map((link) => (
            <LinkRow key={link.id} link={link} target={target} />
          ))}
        </ul>
      )}
    </div>
  );
};

const LinkRow: React.FC<{
  link: ResourceActiveShareLink;
  target: ShareTarget;
}> = ({ link, target }) => {
  const client = useTenantedClient();
  const analytics = useAnalytics();
  const orgName = useWhoami((s) => s.org.name);
  const meId = useWhoami((s) => s.whoami.id);
  const { refresh } = useApplyShareChange([target]);
  const [revoking, setRevoking] = useState(false);

  // An emailed invite opens for one address, so it is named by that address.
  const invite = link.invitedEmail;
  const who = invite !== null ? invite : audienceLabel(link.audience, orgName);
  const what =
    link.shareMode === 'copy'
      ? 'can make a copy'
      : permissionLabel(toSharePermission(link.permission)).toLowerCase();
  const creator =
    link.createdBy.userId === meId
      ? 'you'
      : link.createdBy.name ?? link.createdBy.email ?? 'someone';
  const opened =
    link.openedByCount === 0
      ? 'Not opened yet'
      : `Opened by ${link.openedByCount} ${
          link.openedByCount === 1 ? 'person' : 'people'
        }`;

  const turnOff = async () => {
    setRevoking(true);
    const result = await client.resource.revokeShareLink({
      shareLinkId: link.id,
    });
    setRevoking(false);
    if (result.isErr()) {
      toast.error(apiErrorMessage(result.error, "Couldn't turn off the link"));
      return;
    }
    toast.success('Link turned off');
    analytics.track(CocoEvent.ShareLinkRevoked, {
      shareMode: link.shareMode,
      openedByCount: link.openedByCount,
    });
    await refresh();
  };

  return (
    <li className="flex items-center gap-3 py-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {invite !== null ? (
          <Mail className="h-4 w-4" />
        ) : link.shareMode === 'copy' ? (
          <Files className="h-4 w-4" />
        ) : (
          <Link2 className="h-4 w-4" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">
          {who} {what}
          {link.targetCount > 1 ? (
            <span className="text-muted-foreground">
              {' '}
              · {link.targetCount} items
            </span>
          ) : null}
        </p>
        <p className="truncate text-xs text-muted-foreground">
          {invite !== null ? 'Added' : 'Made'} by {creator} · {opened} · expires
          in {formatDistanceToNowStrict(new Date(link.expiresAt))}
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        loading={revoking}
        onClick={() => void turnOff()}
      >
        Turn off
      </Button>
    </li>
  );
};
